// IM-XPR-006 (IM-UX-131..140) — "Encontrar empresas para vender" com três intenções explícitas e continuidade visual:
// Minhas empresas (carteira, visão de trabalho), Analisar uma empresa (inclusão individual curta) e Descobrir novas
// empresas (descoberta em lote pelo motor existente). Tudo acontece DENTRO do objetivo (/objetivos/empresas/...): mesma
// trilha, mesmo cabeçalho, volta à visão do objetivo sem faixa de correção. Estágios e contadores vêm de uma única
// contagem do servidor (targetStageCounts); nenhum motor é duplicado aqui.
import { useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { fitReading, objectiveOf, stageNextAction, TARGET_STAGE_LABEL, TARGET_STAGE_TONE, type TargetStageKey } from '@intelimove/contracts';
import { AsyncView, EmptyState } from '../components/States';
import { TargetDiscoveryPanel } from '../components/TargetDiscovery';
import { fmtDate, intelApi } from '../data/intel';
import { useResource } from '../hooks/useResource';
import { NotFound } from '../pages/NotFound';
import { AddCompetitor } from '../pages/context/Competitors';
import { FitBlocker, TARGET_FINDER, TargetDetail, useCanManage, useFitReadiness } from '../pages/Targets';
import { ObjectiveHead } from './ObjectiveWorkspace';
import { plural } from './data';
import { useExperience } from './ExperienceContext';

type Any = any;
type Stages = Record<TargetStageKey, number> & { total: number };
export const OBJECTIVE_BASE = '/objetivos/empresas';
export const dossierHref = (id: string) => `${OBJECTIVE_BASE}/empresa/${id}`;
const SECTIONS = [
  { key: 'carteira', label: 'Minhas empresas' },
  { key: 'analisar', label: 'Analisar uma empresa' },
  { key: 'descobrir', label: 'Descobrir novas empresas' },
] as const;
type SectionKey = (typeof SECTIONS)[number]['key'];

function useStages() {
  return useResource(async (s): Promise<{ stages: Stages; waiting: Any[] }> => {
    const [p, w] = await Promise.all([
      intelApi.pipelineSummary(s),
      intelApi.targets({ stage: 'AGUARDANDO_DECISAO', limit: 4 }, s).then((x) => x.items).catch(() => []),
    ]);
    return { stages: (p as Any).stages as Stages, waiting: w };
  }, []);
}

// ── Visão do objetivo ─────────────────────────────────────────────────────────────────────────────────────────────
export function TargetsObjective() {
  const def = objectiveOf('empresas')!;
  const canManage = useCanManage();
  const fit = useFitReadiness();
  const { state, reload } = useStages();
  const st = state.kind === 'ready' ? state.data.stages : null;
  const working = st ? st.EM_ANALISE + st.AGUARDANDO_DECISAO : 0;
  return (
    <section aria-labelledby="page-title">
      <ObjectiveHead def={def} journey={null} hideAnalyses>
        <Link className="xp-primary petrol" to={`${OBJECTIVE_BASE}/carteira`}>Ver minhas empresas{st && working ? <span className="xp-count"> · {plural(working, 'empresa em análise', 'empresas em análise')}</span> : null}</Link>
      </ObjectiveHead>
      <div className="xp-shell">
        {fit && !fit.ready && <FitBlocker fit={fit} />}
        <div className="xp-intents" role="list" aria-label="O que você quer fazer">
          <IntentCard to={`${OBJECTIVE_BASE}/carteira`} title="Minhas empresas" text="Veja e continue o trabalho nas empresas que você já encontrou.">
            <AsyncView state={state} onRetry={reload} empty={null} loading="metrics">
              {(d) => (
                <dl className="xp-intent-counts">
                  <div><dt>Total</dt><dd>{d.stages.total}</dd></div>
                  <div><dt>Aguardando decisão</dt><dd>{d.stages.AGUARDANDO_DECISAO}</dd></div>
                  <div><dt>Aprovadas</dt><dd>{d.stages.APROVADA}</dd></div>
                  <div><dt>Em acompanhamento</dt><dd>{d.stages.EM_ACOMPANHAMENTO}</dd></div>
                </dl>
              )}
            </AsyncView>
          </IntentCard>
          <IntentCard to={`${OBJECTIVE_BASE}/analisar`} title="Analisar uma empresa" text="Informe uma empresa que você já conhece e deixe o InteliMove preparar o dossiê.">
            <p className="xp-quiet">Nome, site ou CNPJ — o CNPJ não é obrigatório.</p>
          </IntentCard>
          <IntentCard to={`${OBJECTIVE_BASE}/descobrir`} title="Descobrir novas empresas" text="Peça ao InteliMove que encontre empresas aderentes ao seu perfil de cliente ideal.">
            <p className="xp-quiet">{fit?.ready ? `Usa ${plural(fit.icps.confirmed, 'perfil de cliente confirmado', 'perfis de cliente confirmados')}, suas ofertas e a região.` : 'Precisa de ao menos um perfil de cliente confirmado.'}</p>
          </IntentCard>
        </div>
        {!canManage && <p className="cell-sub">Incluir e descobrir empresas é feito por administradores da organização. Você vê e acompanha toda a carteira da equipe.</p>}
        {state.kind === 'ready' && state.data.waiting.length > 0 && (
          <article className="xp-panel" aria-labelledby="xp-waiting">
            <span className="xp-kicker">Próximo passo</span>
            <h2 id="xp-waiting">{plural(state.data.stages.AGUARDANDO_DECISAO, 'empresa aguardando sua decisão', 'empresas aguardando sua decisão')}</h2>
            <ul className="xp-list" role="list">
              {state.data.waiting.map((t: Any) => (
                <li key={t.id} className="xp-row">
                  <div className="xp-row-main"><b>{t.name}</b><span>{fitReading(t.fit).headline} · cobertura {fitReading(t.fit).coveragePct}%</span></div>
                  <div className="xp-row-side"><Link to={dossierHref(t.id)} aria-label={`Abrir dossiê de ${t.name}`}>Abrir dossiê</Link></div>
                </li>
              ))}
            </ul>
            {state.data.stages.AGUARDANDO_DECISAO > state.data.waiting.length && <Link className="link-btn" to={`${OBJECTIVE_BASE}/carteira?estagio=AGUARDANDO_DECISAO`}>Ver todas aguardando decisão</Link>}
          </article>
        )}
        <details className="xp-why">
          <summary>Como funciona</summary>
          <p className="xp-quiet">O InteliMove pesquisa cada empresa em fontes públicas, compara com o seu perfil de cliente ideal e monta um dossiê. Você decide quem vale abordar; só então procura as pessoas certas. Nada é enviado a nenhum CRM sem você pedir. Empresas, pesquisas e decisões são da organização: todos os membros veem a mesma carteira.</p>
        </details>
      </div>
    </section>
  );
}

function IntentCard({ to, title, text, children }: { to: string; title: string; text: string; children?: ReactNode }) {
  return (
    <article className="xp-intent" role="listitem">
      <h2><Link to={to} className="xp-intent-link">{title}</Link></h2>
      <p>{text}</p>
      {children}
    </article>
  );
}

// ── Seções do objetivo (mesmo cabeçalho e trilha) ─────────────────────────────────────────────────────────────────
function SectionFrame({ current, title, lead, children }: { current: SectionKey | 'dossie'; title: string; lead?: string; children: ReactNode }) {
  const def = objectiveOf('empresas')!;
  return (
    <section aria-labelledby="page-title" className="xp-targets">
      <nav className="xp-trail" aria-label="Você está em">
        <ol>
          <li><Link to="/objetivos">Meus objetivos</Link></li>
          <li><Link to={OBJECTIVE_BASE}>{def.title}</Link></li>
          {current !== 'dossie' && <li aria-current="page">{SECTIONS.find((s) => s.key === current)!.label}</li>}
          {current === 'dossie' && <li><Link to={`${OBJECTIVE_BASE}/carteira`}>Minhas empresas</Link></li>}
        </ol>
      </nav>
      {current !== 'dossie' && (
        <>
          <h1 id="page-title" className="xp-title">{title}</h1>
          {lead && <p className="xp-lead">{lead}</p>}
          <nav className="xp-intent-tabs" aria-label="Encontrar empresas para vender">
            {SECTIONS.map((s) => <NavLink key={s.key} to={`${OBJECTIVE_BASE}/${s.key}`} className={({ isActive }) => (isActive ? 'active' : '')}>{s.label}</NavLink>)}
          </nav>
        </>
      )}
      {children}
    </section>
  );
}

export function TargetsSection() {
  const { secao = '' } = useParams();
  if (secao === 'carteira') return <SectionFrame current="carteira" title="Minhas empresas" lead="Toda a carteira da sua organização: o que já foi encontrado, o que espera decisão e o que está em acompanhamento."><Portfolio /></SectionFrame>;
  if (secao === 'analisar') return <SectionFrame current="analisar" title="Analisar uma empresa" lead="Informe uma empresa que você já conhece e deixe o InteliMove preparar o dossiê."><AnalyzeOne /></SectionFrame>;
  if (secao === 'descobrir') return <SectionFrame current="descobrir" title="Descobrir novas empresas" lead="Peça ao InteliMove que encontre empresas aderentes ao seu perfil de cliente ideal."><Discover /></SectionFrame>;
  return <NotFound />;
}

export function TargetDossierRoute() {
  return <SectionFrame current="dossie" title=""><TargetDetail inObjective /></SectionFrame>;
}

// ── Minhas empresas ───────────────────────────────────────────────────────────────────────────────────────────────
const STAGE_FILTERS: Array<[string, string]> = [['', 'Ativas'], ['AGUARDANDO_DECISAO', TARGET_STAGE_LABEL.AGUARDANDO_DECISAO], ['EM_ANALISE', TARGET_STAGE_LABEL.EM_ANALISE], ['APROVADA', 'Aprovadas'], ['EM_ACOMPANHAMENTO', TARGET_STAGE_LABEL.EM_ACOMPANHAMENTO], ['REJEITADA', 'Rejeitadas'], ['ARQUIVADA', 'Arquivadas']];
const PAGE = 24;

function Portfolio() {
  const opportunities = useExperience().opportunities;
  const [params, setParams] = useSearchParams();
  const stage = STAGE_FILTERS.some(([k]) => k === params.get('estagio')) ? params.get('estagio')! : '';
  const fit = params.get('aderencia') ?? '';
  const offset = Math.max(0, Number(params.get('p') ?? 0) || 0);
  const [q, setQ] = useState(params.get('q') ?? '');
  const set = (k: string, v: string) => { const p = new URLSearchParams(params); if (v) p.set(k, v); else p.delete(k); if (k !== 'p') p.delete('p'); setParams(p, { replace: true }); };
  // Busca com pequena espera para não consultar a cada tecla.
  useEffect(() => { const h = setTimeout(() => { if ((params.get('q') ?? '') !== q.trim()) set('q', q.trim()); }, 350); return () => clearTimeout(h); }, [q]);
  const [order, setOrder] = useState<'PORTFOLIO' | 'OPPORTUNITY'>('PORTFOLIO');
  const { state, reload, refresh } = useResource(async (s) => {
    const query = { stage: stage || undefined, fit: fit || undefined, q: params.get('q') || undefined, limit: PAGE, offset, order };
    if (!opportunities) return intelApi.targets(query, s);
    const page = await intelApi.opportunities(query, s);
    return { ...page, items: page.items.map((x) => ({ ...x.target, opportunity: x.assessment })) };
  }, [stage, fit, params.get('q'), offset, order, opportunities]);
  const researching = state.kind === 'ready' && state.data.items.some((t: Any) => t.stage === 'EM_ANALISE' && ['QUEUED', 'RUNNING'].includes(t.latestRun?.status));
  useEffect(() => { if (!researching) return; const h = setInterval(refresh, 5000); return () => clearInterval(h); }, [researching, refresh]);
  const stages = state.kind === 'ready' ? (state.data as Any).stages as Stages : null;
  return (
    <>
      <div className="xp-portfolio-tools">
        <label className="field xp-search"><span className="sr-only">Buscar na carteira</span><input type="search" placeholder="Buscar por nome, site ou segmento" value={q} onChange={(e) => setQ(e.target.value)} /></label>
        <label className="field xp-fit-filter"><span className="sr-only">Filtrar por aderência</span>
          <select value={fit} onChange={(e) => set('aderencia', e.target.value)}><option value="">Qualquer aderência</option><option value="ALTA">Aderência alta</option><option value="MEDIA">Aderência média</option><option value="BAIXA">Aderência baixa</option><option value="INDETERMINADA">Ainda indeterminada</option></select></label>
        {opportunities && <label className="field xp-fit-filter"><span className="sr-only">Ordenar empresas</span><select value={order} onChange={(e) => setOrder(e.target.value as typeof order)}><option value="PORTFOLIO">Ordem da carteira</option><option value="OPPORTUNITY">Prioridade recomendada</option></select></label>}
        <a className="link-btn" href="/api/v1/targets/export.xlsx" download>Exportar (XLSX)</a>
      </div>
      <div className="xp-stage-filter" role="group" aria-label="Filtrar por situação">
        {STAGE_FILTERS.map(([k, label]) => {
          const n = stages ? (k ? stages[k as TargetStageKey] : stages.total) : null;
          return <button key={k || 'all'} type="button" className={stage === k ? 'active' : ''} aria-pressed={stage === k} onClick={() => set('estagio', k)}>{label}{n !== null ? <span className="xp-n">{n}</span> : null}</button>;
        })}
      </div>
      <AsyncView state={state} onRetry={reload} empty={null}>
        {(d) => !d.items.length ? (
          <EmptyState title={params.get('q') || stage || fit ? 'Nenhuma empresa neste filtro' : 'Sua carteira ainda está vazia'}>
            {params.get('q') || stage || fit ? 'Limpe a busca ou escolha outra situação.' : <>Comece por <Link to={`${OBJECTIVE_BASE}/analisar`}>analisar uma empresa que você já conhece</Link> ou <Link to={`${OBJECTIVE_BASE}/descobrir`}>descobrir novas empresas</Link>.</>}
          </EmptyState>
        ) : (
          <>
            <p className="cell-sub" aria-live="polite">{plural(d.total, 'empresa', 'empresas')}{stage ? ` · ${STAGE_FILTERS.find(([k]) => k === stage)![1].toLowerCase()}` : ''}</p>
            <ul className="xp-company-grid" role="list">
              {d.items.map((t: Any) => <CompanyCard key={t.id} t={t} />)}
            </ul>
            <div className="load-more">
              {offset > 0 && <button className="secondary" onClick={() => set('p', String(Math.max(0, offset - PAGE)))}>Anteriores</button>}
              {offset + PAGE < d.total && <button className="secondary" onClick={() => set('p', String(offset + PAGE))}>Próximas</button>}
            </div>
          </>
        )}
      </AsyncView>
    </>
  );
}

function CompanyCard({ t }: { t: Any }) {
  const stage = (t.stage ?? 'EM_ANALISE') as TargetStageKey;
  const r = fitReading(t.fit);
  const researching = ['QUEUED', 'RUNNING'].includes(t.latestRun?.status);
  return (
    <li className="xp-company">
      <div className="xp-company-top">
        <h2><Link to={dossierHref(t.id)}>{t.name}</Link></h2>
        <span className={`tag ${TARGET_STAGE_TONE[stage]}`}>{researching && <i className="spin" aria-hidden />}{TARGET_STAGE_LABEL[stage]}</span>
      </div>
      <p className="cell-sub wide">{[t.domain, t.segment, [t.city, t.uf].filter(Boolean).join('/')].filter(Boolean).join(' · ') || 'Sem dados públicos ainda'}</p>
      <dl className="xp-company-facts">
        {t.opportunity && <div><dt>Prioridade</dt><dd>{t.opportunity.classificationLabel.replace('Oportunidade ', '')}</dd></div>}
        <div><dt>Aderência</dt><dd><span className={`dot ${r.tone}`} aria-hidden />{r.headline.replace('Aderência ', '').replace(/^./, (c) => c.toUpperCase())}</dd></div>
        <div><dt>Cobertura</dt><dd>{r.total ? `${r.coveragePct}%` : '—'}</dd></div>
        <div><dt>Confiança</dt><dd>{r.confidenceLabel}</dd></div>
        <div><dt>Pessoas</dt><dd>{t.peopleCount ?? 0}</dd></div>
      </dl>
      <p className="xp-company-next"><b>Próxima ação:</b> {t.opportunity?.nextActionLabel ?? stageNextAction(stage, { researching, people: t.peopleCount ?? 0 })}</p>
      {t.opportunity && <p className="cell-sub wide">{t.opportunity.summary}</p>}
      <div className="xp-company-foot">
        <span className="cell-sub">Último movimento {fmtDate(t.updatedAt)}{t.createdByName ? ` · incluída por ${t.createdByName}` : ''}</span>
        <Link className="small-action" to={dossierHref(t.id)} aria-label={`Abrir dossiê de ${t.name}`}>Abrir dossiê</Link>
      </div>
    </li>
  );
}

// ── Analisar uma empresa (inclusão individual) ────────────────────────────────────────────────────────────────────
function AnalyzeOne() {
  const nav = useNavigate();
  const canManage = useCanManage();
  const [params] = useSearchParams();
  if (!canManage) return <p className="card-sub">Um administrador da organização inclui empresas. Você pode ver a carteira e os dossiês em <Link to={`${OBJECTIVE_BASE}/carteira`}>Minhas empresas</Link>.</p>;
  return (
    <div className="xp-analyze">
      <ol className="xp-steps-mini" aria-label="Como funciona">
        <li>Informe nome, site ou CNPJ</li><li>Confirme a empresa certa</li><li>O InteliMove prepara o dossiê</li>
      </ol>
      <AddCompetitor finder={{ ...TARGET_FINDER, title: 'Qual empresa você quer analisar?', cta: 'Preparar dossiê' }} known={[]} initialQuery={params.get('incluir')?.slice(0, 200) ?? ''}
        onCancel={() => nav(OBJECTIVE_BASE)} onDone={(id) => nav(dossierHref(id))} onOpenExisting={(id) => nav(dossierHref(id))} />
    </div>
  );
}

// ── Descobrir novas empresas (motor de descoberta existente) ──────────────────────────────────────────────────────
function Discover() {
  const nav = useNavigate();
  const canManage = useCanManage();
  const fit = useFitReadiness();
  const [params] = useSearchParams();
  if (!canManage) return <p className="card-sub">Um administrador da organização pede descobertas. As empresas encontradas aparecem em <Link to={`${OBJECTIVE_BASE}/carteira`}>Minhas empresas</Link>.</p>;
  return (
    <>
      {fit && !fit.ready && <FitBlocker fit={fit} />}
      <TargetDiscoveryPanel onOpen={(id) => nav(dossierHref(id))} fit={fit} presetSegment={params.get('segmento')?.slice(0, 120) ?? ''} />
      <p className="cell-sub"><Link to="/empresas-alvo/descobertas-anteriores">Descobertas anteriores</Link></p>
    </>
  );
}
